import React, { useState, useEffect } from 'react';
import { ThumbsUp, MessageCircle, Bell, BellOff } from 'lucide-react';
import api from '../api';
import MapScreen from './MapScreen';
import Reports from './Reports';
import '../components/mapScreenStyles.css';

const Home = ({ user, logout }) => {
  const [activeTab, setActiveTab] = useState('map'); // 'map', 'reports', 'profile'
  const [activeReports, setActiveReports] = useState([]);
  const [deletedReports, setDeletedReports] = useState([]);
  const [isLoadingActive, setIsLoadingActive] = useState(false);
  const [isLoadingDeleted, setIsLoadingDeleted] = useState(false);
  const [error, setError] = useState(null);
  const [reportsTab, setReportsTab] = useState(''); // '' (hidden), 'active' or 'deleted'
  const [showWelcome, setShowWelcome] = useState(() => {
    // Only show if user exists and hasn't been shown this session
    const hasShownWelcome = sessionStorage.getItem('hasShownWelcome');
    return user && !hasShownWelcome;
  });
  const [welcomeFading, setWelcomeFading] = useState(false);
  const [reportAddresses, setReportAddresses] = useState({}); // Store addresses for reports
  const [alertRadius, setAlertRadius] = useState(() => {
    // Load from localStorage or default to 500m
    return parseInt(localStorage.getItem('alertRadius')) || 500;
  });
  const [locationRefreshInterval, setLocationRefreshInterval] = useState(() => {
    // Load from localStorage or default to 10 seconds
    return parseInt(localStorage.getItem('locationRefreshInterval')) || 10;
  });
  const [alertsMuted, setAlertsMuted] = useState(() => {
    // Load from localStorage or default to false
    return localStorage.getItem('alertsMuted') === 'true';
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleteReports, setDeleteReports] = useState(false);
  const [deleteComments, setDeleteComments] = useState(false);
  const [deleteUpvotes, setDeleteUpvotes] = useState(false);
  const [trackingMode, setTrackingMode] = useState(() => {
    return localStorage.getItem('trackingMode') === 'true';
  });
  const [showAccuracyCircle, setShowAccuracyCircle] = useState(() => {
    return localStorage.getItem('showAccuracyCircle') === 'true';
  });
  const [showLocationTrail, setShowLocationTrail] = useState(() => {
    return localStorage.getItem('showLocationTrail') === 'true';
  });
  const [showModeratorCoverageCircle, setShowModeratorCoverageCircle] = useState(() => {
    return localStorage.getItem('showModeratorCoverageCircle') === 'true';
  });

  // Manage welcome popup display and fading
  useEffect(() => {
    if (user && showWelcome) {
      // Mark as shown in sessionStorage (persists until browser closed)
      sessionStorage.setItem('hasShownWelcome', 'true');
      
      // Start fading after 2 seconds
      const fadeTimer = setTimeout(() => {
        setWelcomeFading(true);
      }, 2000);
      
      // Hide completely after 3 seconds (fading takes 1 second)
      const hideTimer = setTimeout(() => {
        setShowWelcome(false);
      }, 3000);
      
      // Clean up timers
      return () => {
        clearTimeout(fadeTimer);
        clearTimeout(hideTimer);
      };
    }
  }, [user, showWelcome]);
  
  // Check if we need to redirect due to missing user
  useEffect(() => {
    if (!user) {
      console.error('Home component loaded without user data');
      // We could redirect to login here, but for now just show an error
      setError('User session not found. Please try logging in again.');
    }
  }, [user]);

  // Fetch user reports when profile tab is activated
  useEffect(() => {
    if (activeTab === 'profile' && user) {
      // Try to find the correct ID property
      const userId = user.id || user._id || user.userId;
      
      if (userId) {
        fetchActiveReports(userId);
        fetchDeletedReports(userId);
      } else {
        console.error('Cannot fetch reports: No user ID available');
        setError('Cannot load reports: User ID not found');
      }
    }
  }, [activeTab, user]);
  
  // Function to fetch active user reports
  const fetchActiveReports = async (userId) => {
    setIsLoadingActive(true);
    setError(null);
    try {
      const response = await api.get(`/api/reports/user/${userId}?status=active`);
      setActiveReports(response.data);
    } catch (err) {
      console.error('Error fetching active reports:', err);
      setError('Unable to load your active reports. Please try again later.');
    } finally {
      setIsLoadingActive(false);
    }
  };
  
  // Function to fetch deleted user reports
  const fetchDeletedReports = async (userId) => {
    setIsLoadingDeleted(true);
    try {
      const response = await api.get(`/api/reports/user/${userId}?status=deleted`);
      setDeletedReports(response.data);
    } catch (err) {
      console.error('Error fetching deleted reports:', err);
    } finally {
      setIsLoadingDeleted(false);
    }
  };
  
  // Function to delete a report (soft delete)
  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    setDeleteLoading(true);
    setDeleteError('');
    try {
      const token = localStorage.getItem('token');
      await api.delete('/api/auth/account', {
        headers: { Authorization: `Bearer ${token}` },
        data: { deleteReports, deleteComments, deleteUpvotes },
      });
      // Clear auth state manually before navigating to avoid route guard conflict
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setShowDeleteModal(false);
      // Hard redirect — bypasses React Router route guard conflict
      window.location.href = '/';
    } catch (err) {
      setDeleteError(err.response?.data?.message || 'Failed to delete account. Please try again.');
      setDeleteLoading(false);
    }
  };

  const deleteReport = async (reportId) => {
    if (window.confirm('Are you sure you want to delete this report? It will be moved to your deleted reports section.')) {
      try {
        await api.delete(`/api/reports/${reportId}`);
        // After successful deletion, update both report lists
        const deletedReport = activeReports.find(report => report.id === reportId);
        if (deletedReport) {
          // Move from active to deleted
          setActiveReports(activeReports.filter(report => report.id !== reportId));
          setDeletedReports([{...deletedReport, isDeleted: true, deletedAt: new Date().toISOString()}, ...deletedReports]);
        }
        alert('Report deleted successfully');
      } catch (err) {
        console.error('Error deleting report:', err);
        alert('Failed to delete the report. Please try again.');
      }
    }
  };
  
  // Function to permanently delete a report
  const permanentlyDeleteReport = async (reportId) => {
    if (window.confirm('Are you sure you want to PERMANENTLY delete this report? This action cannot be undone.')) {
      try {
        await api.delete(`/api/reports/hard/${reportId}`);
        // After successful deletion, update the deleted reports list
        setDeletedReports(deletedReports.filter(report => report.id !== reportId));
        alert('Report permanently deleted');
      } catch (err) {
        console.error('Error permanently deleting report:', err);
        alert('Failed to permanently delete the report. Please try again.');
      }
    }
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  // Load addresses from reports (use stored addresses instead of fetching)
  useEffect(() => {
    const newAddresses = {};
    
    [...activeReports, ...deletedReports].forEach(report => {
      if (report.address) {
        // Use stored address from database
        newAddresses[report.id] = report.address;
      } else if (report.position && !reportAddresses[report.id]) {
        // Fallback: show coordinates if no address stored
        newAddresses[report.id] = `${report.position.lat.toFixed(4)}, ${report.position.lng.toFixed(4)}`;
      }
    });
    
    if (Object.keys(newAddresses).length > 0) {
      setReportAddresses(prev => ({ ...prev, ...newAddresses }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeReports, deletedReports]);
  
  // Get type label based on severity level
  const getTypeLabel = (type) => {
    const typeLabels = {
      'level1': 'Low Severity',
      'level2': 'Medium Severity',
      'level3': 'High Severity'
    };
    return typeLabels[type] || type;
  };

  return (
    <>
    <div className="dashboard-container">
      {/* Splash Screen */}
      {showWelcome && user && (
        <div className={`welcome-popup ${welcomeFading ? 'fade-out' : ''}`}>
          <div className="welcome-content">
            <h2>Welcome, {user.name}!</h2>
            <p>SafeHER is here to help you stay safe.</p>
          </div>
        </div>
      )}
      
      <div style={{ display: activeTab === 'map' ? 'flex' : 'none', flex: 1, overflow: 'hidden' }}>
        <MapScreen 
          user={user} 
          onNavigateToProfile={() => setActiveTab('profile')}
          onNavigateToReports={() => setActiveTab('reports')}
          onNavigateToModeratorDashboard={() => (window.location.href = '/moderator/dashboard')}
          onLogout={logout}
          alertRadius={alertRadius}
          locationRefreshInterval={locationRefreshInterval}
          alertsMuted={alertsMuted}
          trackingMode={trackingMode}
          showAccuracyCircle={showAccuracyCircle}
          showLocationTrail={showLocationTrail}
          showModeratorCoverageCircle={showModeratorCoverageCircle}
        />
      </div>
      
      {activeTab === 'reports' && (
        <div style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 60px)' }}>
          <Reports user={user} />
        </div>
      )}
      
      {activeTab === 'profile' && (
        <div className="profile-container" style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 60px)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
            <h3 style={{ margin: 0 }}>My Profile</h3>
            <button
              type="button"
              className="logout-btn"
              onClick={() => setActiveTab('map')}
              style={{
                padding: '8px 12px',
                backgroundColor: '#688A65',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title="Back to Map"
            >
              Map
            </button>
          </div>
          {user && (
            <div className="profile-info">
              <div className="info-group">
                <label>User ID</label>
                <p>{user.id || user._id || user.userId || 'Not available'}</p>
              </div>
              <div className="info-group">
                <label>Name</label>
                <p>{user.name}</p>
              </div>
              <div className="info-group">
                <label>Email</label>
                <p>{user.email}</p>
              </div>
              <div className="info-group">
                <label>Location</label>
                <p>{user.location}</p>
              </div>
              
              <div className="profile-actions" style={{ marginTop: '20px', marginBottom: '20px' }}>
                <button 
                  className="logout-btn" 
                  onClick={() => setAlertsMuted(!alertsMuted)}
                  style={{
                    flex: 1,
                    padding: '10px 15px',
                    backgroundColor: alertsMuted ? '#dc3545' : '#688A65',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: '500',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  {alertsMuted ? <BellOff size={16} strokeWidth={2} /> : <Bell size={16} strokeWidth={2} />}
                  {alertsMuted ? 'Alerts Muted' : 'Alerts Active'}
                </button>
              </div>
              <p style={{ fontSize: '12px', color: '#BFC1A8', marginTop: '5px' }}>
                {alertsMuted ? 'You will not receive proximity alerts' : 'You will be alerted about nearby incidents'}
              </p>
              <div className="info-group">
                <label>Alert Radius</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type="range"
                    min="100"
                    max="2000"
                    step="100"
                    value={alertRadius}
                    onChange={(e) => {
                      const newRadius = parseInt(e.target.value);
                      setAlertRadius(newRadius);
                      localStorage.setItem('alertRadius', newRadius);
                    }}
                    style={{ flex: 1 }}
                  />
                  <span style={{ minWidth: '80px', fontSize: '14px', color: '#97AC82' }}>
                    {alertRadius}m
                  </span>
                </div>
                <p style={{ fontSize: '12px', color: '#BFC1A8', marginTop: '5px' }}>
                  You'll be alerted when reports are within {alertRadius} meters of your location
                </p>
              </div>

              {(user?.role === 'moderator' || user?.moderatorActive) && (
                <div className="info-group">
                  <label>Moderator Coverage</label>
                  <button
                    onClick={() => {
                      const newValue = !showModeratorCoverageCircle;
                      setShowModeratorCoverageCircle(newValue);
                      localStorage.setItem('showModeratorCoverageCircle', newValue);
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 15px',
                      backgroundColor: showModeratorCoverageCircle ? '#688A65' : '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: '500',
                      transition: 'all 0.2s'
                    }}
                  >
                    My Coverage Circle: {showModeratorCoverageCircle ? 'ON' : 'OFF'}
                  </button>
                  <p style={{ fontSize: '12px', color: '#BFC1A8', marginTop: '5px' }}>
                    Show your approved moderator coverage radius on the map
                  </p>
                </div>
              )}
              
              <div className="info-group">
                <label>Location Update Frequency</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type="range"
                    min="5"
                    max="60"
                    step="5"
                    value={locationRefreshInterval}
                    onChange={(e) => {
                      const newInterval = parseInt(e.target.value);
                      setLocationRefreshInterval(newInterval);
                      localStorage.setItem('locationRefreshInterval', newInterval);
                    }}
                    style={{ flex: 1 }}
                  />
                  <span style={{ minWidth: '80px', fontSize: '14px', color: '#97AC82' }}>
                    {locationRefreshInterval}s
                  </span>
                </div>
                <p style={{ fontSize: '12px', color: '#BFC1A8', marginTop: '5px' }}>
                  {locationRefreshInterval <= 10 ? 'Faster updates, more battery usage' : 
                   locationRefreshInterval <= 30 ? 'Balanced updates and battery' : 
                   'Slower updates, saves battery'}
                </p>
              </div>
              
              {/* <div className="info-group">
                <label>Live Tracking</label>
                <button
                  onClick={() => {
                    const newValue = !trackingMode;
                    setTrackingMode(newValue);
                    localStorage.setItem('trackingMode', newValue);
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 15px',
                    backgroundColor: trackingMode ? '#688A65' : '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: '500',
                    transition: 'all 0.2s'
                  }}
                >
                  Tracking: {trackingMode ? 'ON' : 'OFF'}
                </button>
                <p style={{ fontSize: '12px', color: '#BFC1A8', marginTop: '5px' }}>
                  Continuously update your location on the map
                </p>
              </div> */}
              
              <div className="info-group">
                <label>Alert Radius</label>
                <button
                  onClick={() => {
                    const newValue = !showAccuracyCircle;
                    setShowAccuracyCircle(newValue);
                    localStorage.setItem('showAccuracyCircle', newValue);
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 15px',
                    backgroundColor: showAccuracyCircle ? '#688A65' : '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: '500',
                    transition: 'all 0.2s'
                  }}
                >
                  Alert Radius: {showAccuracyCircle ? 'ON' : 'OFF'}
                </button>
                <p style={{ fontSize: '12px', color: '#BFC1A8', marginTop: '5px' }}>
                  Show GPS accuracy radius around your location
                </p>
              </div>
              
              {/* <div className="info-group">
                <label>Location Trail</label>
                <button
                  onClick={() => {
                    const newValue = !showLocationTrail;
                    setShowLocationTrail(newValue);
                    localStorage.setItem('showLocationTrail', newValue);
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 15px',
                    backgroundColor: showLocationTrail ? '#688A65' : '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: '500',
                    transition: 'all 0.2s'
                  }}
                >
                  Trail: {showLocationTrail ? 'ON' : 'OFF'}
                </button>
                <p style={{ fontSize: '12px', color: '#BFC1A8', marginTop: '5px' }}>
                  Show your movement path on the map
                </p>
              </div> */}
              
              <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div className="profile-actions">
                  <button className="logout-btn" onClick={logout}>Logout</button>
                </div>
                <button
                  onClick={() => { setShowDeleteModal(true); setDeleteConfirmText(''); setDeleteError(''); setDeleteReports(false); setDeleteComments(false); setDeleteUpvotes(false); }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: 'transparent',
                    color: '#dc3545',
                    border: '1px solid #dc3545',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: '500',
                    fontSize: '14px',
                  }}
                >
                  Delete Account
                </button>
              </div>
              
              {/* My Reports removed - now available as separate page */}
              {false && (
                <div className="my-reports-section">
                <h4>My Reports</h4>
                
                <div className="reports-tabs">
                  <button 
                    className={`report-tab-btn ${reportsTab === 'active' ? 'active' : ''}`}
                    onClick={() => setReportsTab('active')}
                  >
                    Active Reports
                  </button>
                  <button 
                    className={`report-tab-btn ${reportsTab === 'deleted' ? 'active' : ''}`}
                    onClick={() => setReportsTab('deleted')}
                  >
                    Deleted Reports
                  </button>
                </div>
                
                {/* Active Reports Tab */}
                {reportsTab === 'active' && (
                  <div className="active-reports-container">
                    {isLoadingActive ? (
                      <p className="loading-text">Loading your active reports...</p>
                    ) : error ? (
                      <p className="error-text">{error}</p>
                    ) : activeReports.length === 0 ? (
                      <p className="no-reports">You don't have any active reports.</p>
                    ) : (
                      <div className="reports-list">
                        {activeReports.map((report) => (
                          <div key={report.id} className="report-item">
                            <div className="report-header">
                              <span className={`report-type ${report.type}`}>{getTypeLabel(report.type)}</span>
                              <span className="report-date">{formatDate(report.timestamp)}</span>
                            </div>
                            <p className="report-description">{report.description}</p>
                            <p className="report-location">
                              Location: {reportAddresses[report.id] || 'Loading address...'}
                            </p>
                            <div className="report-stats" style={{ display: 'flex', gap: '15px', margin: '10px 0', fontSize: '14px', color: '#97AC82' }}>
                              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <ThumbsUp size={14} strokeWidth={2} />
                                {report.upvoteCount || 0} upvote{(report.upvoteCount || 0) !== 1 ? 's' : ''}
                              </span>
                              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <MessageCircle size={14} strokeWidth={2} />
                                {report.comments?.length || 0} comment{(report.comments?.length || 0) !== 1 ? 's' : ''}
                              </span>
                            </div>
                            <button 
                              className="delete-report-btn" 
                              onClick={() => deleteReport(report.id)}
                            >
                              Delete Report
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Deleted Reports Tab */}
                {reportsTab === 'deleted' && (
                  <div className="deleted-reports-container">
                    {isLoadingDeleted ? (
                      <p className="loading-text">Loading your deleted reports...</p>
                    ) : deletedReports.length === 0 ? (
                      <p className="no-reports">You don't have any deleted reports.</p>
                    ) : (
                      <div className="reports-list">
                        {deletedReports.map((report) => (
                          <div key={report.id} className="report-item deleted">
                            <div className="report-header">
                              <span className={`report-type ${report.type}`}>{getTypeLabel(report.type)}</span>
                              <div className="report-dates">
                                <span className="report-date">Created: {formatDate(report.timestamp)}</span>
                                <span className="report-date">Deleted: {formatDate(report.deletedAt)}</span>
                              </div>
                            </div>
                            <p className="report-description">{report.description}</p>
                            <p className="report-location">
                              Location: {reportAddresses[report.id] || 'Loading address...'}
                            </p>
                            <button 
                              className="delete-report-btn permanent" 
                              onClick={() => permanentlyDeleteReport(report.id)}
                            >
                              Permanently Delete
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
      {/* ── Delete Account Modal ── */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
          padding: '20px',
        }}>
          <div style={{
            backgroundColor: '#fff', borderRadius: '12px', padding: '28px',
            width: '100%', maxWidth: '420px', boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          }}>
            <h3 style={{ margin: '0 0 8px', color: '#dc3545' }}>Delete account?</h3>
            <p style={{ fontSize: '13px', color: '#555', margin: '0 0 12px', lineHeight: '1.5' }}>
              Your account and personal data will be permanently deleted immediately.
              Community data (reports, comments, upvotes) is anonymised by default — check below to request full deletion.
            </p>

            {/* Checkbox options */}
            <div style={{ background: '#fff5f5', border: '1px solid #f5c6cb', borderRadius: '8px', padding: '12px', marginBottom: '12px' }}>
              <p style={{ fontSize: '12px', fontWeight: 600, margin: '0 0 10px', color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Also request deletion of:
              </p>
              {[
                { key: 'reports', label: 'My submitted reports', state: deleteReports, set: setDeleteReports, note: 'Reports will be removed from the map' },
                { key: 'comments', label: 'My comments on reports', state: deleteComments, set: setDeleteComments, note: 'Comments will be removed from all reports' },
                { key: 'upvotes', label: 'My upvotes', state: deleteUpvotes, set: setDeleteUpvotes, note: 'Upvote counts on reports will be adjusted' },
              ].map(({ key, label, state, set, note }) => (
                <label key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={state}
                    onChange={e => set(e.target.checked)}
                    style={{ marginTop: '2px', accentColor: '#dc3545', width: '15px', height: '15px', flexShrink: 0 }}
                  />
                  <span>
                    <span style={{ fontSize: '13px', fontWeight: 500, color: '#333' }}>{label}</span>
                    <span style={{ display: 'block', fontSize: '11px', color: '#888', marginTop: '1px' }}>{note}</span>
                  </span>
                </label>
              ))}
              <p style={{ fontSize: '11px', color: '#888', margin: '8px 0 0', lineHeight: '1.4' }}>
                Requested deletions may take up to 90 days to be removed from all backups and caches.
              </p>
            </div>

            <p style={{ fontSize: '13px', fontWeight: 600, margin: '4px 0 6px' }}>
              Type DELETE to confirm:
            </p>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={e => setDeleteConfirmText(e.target.value.toUpperCase())}
              placeholder="DELETE"
              autoFocus
              style={{
                width: '100%', padding: '10px 12px', fontSize: '14px',
                border: '1.5px solid #dc3545', borderRadius: '6px',
                outline: 'none', boxSizing: 'border-box',
                backgroundColor: '#fff5f5',
              }}
            />
            {deleteError && (
              <p style={{ fontSize: '13px', color: '#dc3545', margin: '8px 0 0' }}>{deleteError}</p>
            )}
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deleteLoading}
                style={{
                  flex: 1, padding: '10px', borderRadius: '6px',
                  border: '1.5px solid #aaa', backgroundColor: '#f0f0f0',
                  cursor: 'pointer', fontWeight: 600, fontSize: '14px',
                  color: '#333',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText !== 'DELETE' || deleteLoading}
                style={{
                  flex: 1, padding: '10px', borderRadius: '6px',
                  border: 'none',
                  backgroundColor: deleteConfirmText === 'DELETE' ? '#dc3545' : '#f5a5a5',
                  color: '#fff', cursor: deleteConfirmText === 'DELETE' ? 'pointer' : 'not-allowed',
                  fontWeight: 600,
                }}
              >
                {deleteLoading ? 'Deleting...' : 'Delete my account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Home;
